import { LoginService } from '../../../services/login.service';
import {
  Component,
  OnInit,
  Input,
  TemplateRef,
  ViewChild,
  AfterViewInit,
  OnDestroy,
  ElementRef,
} from '@angular/core';
import { ConfigService } from '../../../services/config.service';
import { CookieService } from 'ngx-cookie-service';
import { FeedbackService } from '../../../services/feedback.service';
import * as moment from 'moment';
import { User } from '../../../models/identity';
import { ActivatedRoute, Router } from '@angular/router';
import { ChatParticipant } from '../../../models/chat/chat-participant.model';
import { ChatMessage } from '../../../models/chat/chat-message.model';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { Observable } from 'rxjs';
import { fromEvent, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { EmojiEvent } from '@ctrl/ngx-emoji-mart/ngx-emoji';
import { emojiFilter } from './emoji-filter';

@Component({
  selector: 'app-feedback-chat-body',
  templateUrl: './feedback-chat-body.component.html',
  styleUrls: ['./feedback-chat-body.component.scss'],
})
export class FeedbackChatBodyComponent
  implements OnInit, OnDestroy, AfterViewInit {
  @Input() events: Observable<string>;
  eventSubscription: Subscription;
  @Input() set isAnonymousVisible(anonymus: boolean) {
    if (anonymus) {
      this.setAnonMessages();
    }
    this._isAnonymousVisible = anonymus;
  }
  @Input() isOpenedByPm = true;
  @Input() senderId? = null;
  @ViewChild('messageInput') messageInput;
  @ViewChild('emojiPopup') emojiPopup: ElementRef;
  @ViewChild('emojiSelect') emojiSelect: ElementRef;
  canShowEmojiPanel: boolean = false;

  public defaultPhoto: string = '../../../assets/images/placeholder_avatar.png';
  public currentChatParticipant: ChatParticipant;
  public chatParticipants: Array<ChatParticipant> = [];
  public searchChats: Array<ChatParticipant> = [];
  public searchString = '';
  public sendMessageText = '';
  public isAnonymousMessage = false;
  public showAnonymForPM = false;
  public teammateInfo = {} as ChatParticipant;
  public selectedMessageId?: number = null;
  public isLeaver: boolean = false;
  modalRef: BsModalRef;

  private _isAnonymousVisible: boolean = false;
  get isAnonymousVisible() {
    return this._isAnonymousVisible;
  }

  private currentUser: User;
  private decimailScale = 10;
  private readonly searchRequestDelayMilliseconds = this.configService
    .configData.searchRequestDelayMilliseconds;
  private readonly anonymPhoto: string =
    '../../../assets/images/svg/anonymous.svg';
  private anonymousChatParticipant: ChatParticipant;
  private timer: any = null;
  private readonly secondsToDisable: number;
  emojiSubs: Subscription;

  emojiIncludeCategories: string[] = ['recent', 'people', 'activity'];
  emojiPanelStyle = {
    width: '154%',
    position: 'absolute',
    bottom: '60px',
    right: '85%',
  };

  constructor(
    private readonly cookieService: CookieService,
    private readonly configService: ConfigService,
    private readonly feedbackService: FeedbackService,
    private route: ActivatedRoute,
    private readonly router: Router,
    private bsModalService: BsModalService,
    private readonly loginService: LoginService
  ) {
    this.secondsToDisable =
      this.configService.configData.feecbackMessageDeleteThresholdMinutes *
      (60 * 1000);
  }

  ngOnInit() {
    this.route.paramMap.subscribe((params) => {
      this.teammateInfo.id = parseInt(
        params.get('teammateEmployeeId'),
        this.decimailScale
      );
      if (params.get('isanonym') === 'true') {
        this.setAnonymity(true);
      }

      if (params.get('senderId')) {
        let senderId = Number(params.get('senderId'));
        this.senderId = senderId;
      }

      this.updateUserChats();
      if (!this.isTeammateMode) {
        return;
      }
      this.getUserPicture(this.teammateInfo);
    });
    this.currentUser = this.loginService.getUser;
  }

  ngAfterViewInit() {
    this.eventSubscription = this.events.subscribe((val) => {
      if (this.messageInput) {
        const nativeElement = this.messageInput
          .nativeElement as HTMLInputElement;
        const caretPosition = nativeElement.selectionStart;
        this.sendMessageText =
          this.sendMessageText.slice(0, caretPosition) +
          val +
          this.sendMessageText.slice(caretPosition);
        setTimeout(() => {
          nativeElement.focus();
        });
      }
    });
    this.emojiSubs = fromEvent<MouseEvent>(document, 'click')
      .pipe(
        filter(() => !!this.emojiPopup),
        filter((event) => {
          const clickTarget = event.target as HTMLElement;
          const notPopup =
            !!this.emojiPopup &&
            this.emojiPopup.nativeElement.contains(clickTarget) === false;
          const notSelect =
            !!this.emojiSelect &&
            this.emojiSelect.nativeElement.contains(clickTarget) === false;
          return notPopup && notSelect;
        })
      )
      .subscribe(() => {
        this.canShowEmojiPanel = false;
      });
  }

  public get isMessageSelected() {
    return this.selectedMessageId != null;
  }

  public get isTeammateMode() {
    return !isNaN(this.teammateInfo.id);
  }

  public get activeChatParticipant() {
    if (this.isAnonymousVisible) {
      return this.anonymousChatParticipant;
    } else {
      return this.currentChatParticipant;
    }
  }
  public get isSearchingReady(): boolean {
    return (
      this.searchString.length >=
      this.configService.configData.minimumSearchStringLength
    );
  }

  getActivePhoto(chatParticipantId: number) {
    const chatParticipant = this.chatParticipants.find(
      (x) => x.id === chatParticipantId
    );
    const activeChatParticipant = chatParticipant
      ? chatParticipant
      : this.teammateInfo;
    return activeChatParticipant.employeePhoto && !this.isAnonymousVisible
      ? activeChatParticipant.employeePhoto
      : this.anonymPhoto;
  }

  public searchContactIfRequired() {
    if (!this.isSearchingReady) {
      this.searchChats = [];
      return;
    }
    if (this.timer != null) {
      clearTimeout(this.timer);
    }
    this.timer = setTimeout(() => {
      this.searchChat();
    }, this.searchRequestDelayMilliseconds);
  }

  public unselectMessageToAction() {
    this.selectedMessageId = null;
  }

  public selectMessageToAction(message: ChatMessage) {
    if (message.personId !== this.loginService.getUser.personId) {
      return;
    }
    if (!this.hasMessageDeletionStatusById(message.id)) {
      return;
    }
    if (this.selectedMessageId === message.id) {
      this.selectedMessageId = null;
      return;
    }
    this.selectedMessageId = message.id;
  }

  public localeTime(source: Date) {
    const appLanguage = this.cookieService.get(
      this.configService.configData.cookieLanguageNameKey
    );
    const offset = moment().utcOffset();
    return moment
      .utc(source)
      .utcOffset(offset)
      .locale(appLanguage)
      .format('H:mm | D MMMM');
  }

  public chooseChat(source: ChatParticipant) {
    if (this.currentChatParticipant) {
      this.currentChatParticipant.messages = [];
    }
    this.currentChatParticipant = source;
    this.feedbackService
      .getMessages(
        source.feedbackChatId,
        this.teammateInfo.id,
        this.isOpenedByPm
      )
      .subscribe((data) => {
        this.currentChatParticipant.messages = data;
        this.messageOrderComparation(this.currentChatParticipant.messages);
      });
  }

  public checkChosenChat(source: ChatParticipant): boolean {
    if (this.currentChatParticipant) {
      return (
        this.currentChatParticipant.feedbackChatId === source.feedbackChatId &&
        this.currentChatParticipant.id === source.id
      );
    }
    return false;
  }

  public getChat(source: ChatParticipant) {
    this.feedbackService
      .getChatId(source, this.teammateInfo.id)
      .subscribe((data) => {
        source.feedbackChatId = this.getDefaultIdIfNull(data);
        this.chooseChat(source);
        if (this.senderId) {
          this.router.navigateByUrl(
            this.router.url.replace(
              this.senderId.toString(),
              this.currentChatParticipant.id.toString()
            )
          );
        }
      });
  }

  public sendMessage(chat: ChatParticipant) {
    if (
      !this.sendMessageText ||
      this.sendMessageText.trim().length == 0 ||
      this.isTeammateMode
    ) {
      return;
    }
    const messageInfo: ChatMessage = {
      feedbackChatParticipantId: this.currentUser.id,
      text: this.sendMessageText,
      displayName: this.currentUser.displayName,
      personId: this.loginService.getUser.personId,
      isActive: true,
    };
    this.feedbackService
      .postMessage(chat, messageInfo, this.isAnonymousMessage)
      .subscribe((data) => {
        chat.feedbackChatId = this.getDefaultIdIfNull(data.feedbackChatId);
        messageInfo.anonymously = this.isAnonymousMessage;
        messageInfo.id = data.message.id;
        messageInfo.isActive = data.message.isActive;
        chat.messages.push(messageInfo);
      });
    this.updateFrontendChats(chat);
    this.sendMessageText = '';
  }

  public checkMineMessage(sourceId: number): boolean {
    return this.loginService.getUser.id === sourceId;
  }

  public setAnonymity(anonymously: boolean) {
    this.setAnonMessages();
    this.isAnonymousVisible = anonymously;
  }

  public deleteMessage() {
    if (!this.hasMessageDeletionStatusById(this.selectedMessageId)) {
      return;
    }
    this.feedbackService
      .deleteMessage(this.selectedMessageId)
      .subscribe((r) => {
        const messageIndex = this.activeChatParticipant.messages.findIndex(
          (msg) => msg.id === this.selectedMessageId
        );
        this.activeChatParticipant.messages.splice(messageIndex, 1);
        this.selectedMessageId = null;
        this.modalRef.hide();
      });
  }

  public hasMessageDeletionStatus(sourceMessage: ChatMessage) {
    return (
      this.secondsToDisable -
        (this.configService.getUtcDate().getTime() -
          new Date(sourceMessage.createdDate).getTime()) >
      0
    );
  }

  public openApproveModal(modalTemplate: TemplateRef<any>) {
    this.modalRef = this.bsModalService.show(modalTemplate);
  }

  private openChatWithSenderId() {
    if (this.senderId) {
      let chat = this.chatParticipants.find((p) => p.id == this.senderId);
      this.getChat(chat);
    }
  }

  private mapAnonChatParticipant(): ChatParticipant {
    return {
      employeePhoto: this.anonymPhoto,
      messages: [],
    };
  }

  private getUserPicture(source: ChatParticipant) {
    if (isNaN(source.id)) {
      return;
    }
    this.feedbackService.getImage(source.id).subscribe((blob) => {
      this.configService.createImageFromBlob(blob, source);
    });
  }

  private updateUserChats() {
    this.feedbackService.getChats(this.teammateInfo.id).subscribe(
      (data) => {
        this.chatParticipants = this.initChatContacts(data);
      },
      null,
      () => this.openChatWithSenderId()
    );
  }

  private searchChat() {
    if (this.isTeammateMode) {
      return;
    }
    if (this.isSearchingReady) {
      this.feedbackService
        .getChatsByPattern(this.searchString)
        .subscribe((data) => {
          this.searchChats = this.initChatContacts(data);
        });
    }
  }

  private initChatContacts(source: Array<ChatParticipant>) {
    source.forEach((x) => {
      if (!x.messages) {
        x.messages = [];
      }
      this.getUserPicture(x);
    });
    return source;
  }

  private containsChatParticipant(
    obj: ChatParticipant,
    array: Array<ChatParticipant>
  ) {
    return array.some(
      (i) => i.id === obj.id && i.displayName === obj.displayName
    );
  }

  private getDefaultIdIfNull(source: any): number {
    if (source) {
      return source;
    }
    return 0;
  }

  private messageOrderComparation(
    currentChatParticipantMessages: Array<ChatMessage>
  ) {
    currentChatParticipantMessages.sort((leftSide, rightSide) => {
      if (leftSide.createdDate < rightSide.createdDate) {
        return -1;
      }
      if (leftSide.createdDate > rightSide.createdDate) {
        return 1;
      }
      return 0;
    });
  }

  private hasMessageDeletionStatusById(
    messageId: number = this.selectedMessageId
  ) {
    const targetMessage = this.activeChatParticipant.messages.find(
      (msg) => msg.id === messageId
    );
    return this.hasMessageDeletionStatus(targetMessage);
  }

  private setAnonMessages() {
    this.anonymousChatParticipant = this.mapAnonChatParticipant();
    this.feedbackService
      .getAnonymousMessages(this.teammateInfo.id)
      .subscribe((messages) => {
        this.anonymousChatParticipant.messages = messages;
        this.messageOrderComparation(this.anonymousChatParticipant.messages);
      });
  }

  private updateFrontendChats(source: ChatParticipant) {
    if (!this.containsChatParticipant(source, this.chatParticipants)) {
      this.chatParticipants.push(source);
    }
  }
  ngOnDestroy() {
    if (this.emojiSubs) {
      this.emojiSubs.unsubscribe();
    }
    if (this.eventSubscription) {
      this.eventSubscription.unsubscribe();
    }
  }

  selectEmoji($event: EmojiEvent) {
    if (this.messageInput) {
      const nativeElement = this.messageInput.nativeElement as HTMLInputElement;
      const caretPosition = nativeElement.selectionStart;
      const emoji = $event.emoji.native;
      this.sendMessageText =
        this.sendMessageText.slice(0, caretPosition) +
        emoji +
        this.sendMessageText.slice(caretPosition);
      this.toggleEmojiPanel();
    }
  }

  toggleEmojiPanel() {
    this.canShowEmojiPanel = !this.canShowEmojiPanel;
  }

  filterEmoji(emoji: string): boolean {
    return emojiFilter(emoji);
  }
}
